import {CompanyType} from 'types/user';
import {CompanyConfig} from '../types';
import {serviceProviderConfig} from './service-provider.config';
import {propertyManagerConfig} from './property-manager.config';
import {deliveryManagerConfig} from './delivery-manager.config';
import {mspProviderConfig} from './msp-provider.config';
import {pickupDropoffConfig} from './pickup-dropoff.config';
import {insuranceAdjusterConfig} from './insurance-adjuster.config';

/**
 * Registry of all company configurations
 */
const companyConfigRegistry: Record<CompanyType, CompanyConfig> = {
  service_provider: serviceProviderConfig,
  property_manager: propertyManagerConfig,
  delivery_manager: deliveryManagerConfig,
  msp_provider: mspProviderConfig,
  pickup_dropoff: pickupDropoffConfig,
  insurance_adjuster: insuranceAdjusterConfig,
};

/**
 * Get configuration for a specific company type
 * Falls back to service_provider config if type is undefined
 */
export function getCompanyConfig(
  companyType: CompanyType | undefined
): CompanyConfig {
  if (!companyType) {
    return companyConfigRegistry.service_provider;
  }
  return (
    companyConfigRegistry[companyType] ?? companyConfigRegistry.service_provider
  );
}

/**
 * Check if a company type has a specific feature enabled
 */
export function hasFeature(
  companyType: CompanyType | undefined,
  feature: keyof CompanyConfig['features']
): boolean {
  const config = getCompanyConfig(companyType);
  return config.features[feature] as boolean;
}

/**
 * Get a label for a specific company type
 */
export function getLabel(
  companyType: CompanyType | undefined,
  key: keyof CompanyConfig['labels']
): string {
  const config = getCompanyConfig(companyType);
  return config.labels[key];
}

// Re-export types for convenience
export type {
  CompanyConfig,
  CompanyLabels,
  FeatureFlags,
  RoleConfig,
} from '../types';

// Re-export individual configs for testing/customization
export {serviceProviderConfig} from './service-provider.config';
export {propertyManagerConfig} from './property-manager.config';
export {deliveryManagerConfig} from './delivery-manager.config';
export {mspProviderConfig} from './msp-provider.config';
export {pickupDropoffConfig} from './pickup-dropoff.config';
export {insuranceAdjusterConfig} from './insurance-adjuster.config';
